// @ts-check
import React from "react"
import { SisuTimeCell } from "../../components/molecules/reports/SisuTimeCell.molecule"
import { TaskCell } from "../../components/molecules/reports/TaskCell.molecule"
import { CategoryCell } from "../../components/molecules/reports/CategoryCell.molecule"
import { numberFormatter } from "../../utils/numbers.utils"
import { ActionsCell } from "../../components/molecules/reports/ActionsCell.molecule"
import { ModuleCell } from "../../components/molecules/reports/ModuleCell.molecule"
import { ProjectCodeCell } from "../../components/molecules/reports/ProjectCodeCell.molecule"
import { applyLeftClickFilter } from "../../utils/tables/filtering.utils"
import { TASK_REPORTS_COMMON_COLUMNS } from "../../consts/modules/taskReports.consts"
import { getDateDDMMYYY } from "../../utils/date.utils"

/**
 * @template TData
 * @param {import("./createTaskReportsCommonCells.factory.types").CreateTaskReportsCommonCellsParams<TData>} params
 */
export function createTaskReportsCommonCells(params) {
  const {
    totals,
    isHoldingModifierDown,
    onLeftClickClientCode,
    userIsAdmin,
    updatingTask,
    contextMenuRef,
    setContextMenuTarget,
  } = params

  /**
   * @param {import("@tanstack/react-table").Table<TData>} table
   * @param {string} value
   */
  function onLeftClickProjectName(table, value) {
    applyLeftClickFilter(table, TASK_REPORTS_COMMON_COLUMNS.projectCode, value)
  }

  /**
   * @param {import("@tanstack/react-table").Table<TData>} table
   * @param {number} value
   */
  function onLeftClickModuleName(table, value) {
    applyLeftClickFilter(table, TASK_REPORTS_COMMON_COLUMNS.module, value)
  }

  /**
   * @param {import("@tanstack/react-table").Table<TData>} table
   * @param {string} value
   */
  function onLeftClickCategoryName(table, value) {
    applyLeftClickFilter(
      table,
      TASK_REPORTS_COMMON_COLUMNS.categoryTitle,
      value
    )
  }

  /** @param {import("@tanstack/react-table").CellContext <TData, string>} ctx */
  const DateCellTemplate = (ctx) => {
    const { getValue } = ctx
    const dateString = getValue()
    const formattedDate = getDateDDMMYYY(dateString)

    return <div>{formattedDate}</div>
  }

  /** @param {import("@tanstack/react-table").CellContext <TData, string>} ctx */
  const ProjectCodeCellTemplate = (ctx) => {
    return (
      <ProjectCodeCell
        ctx={ctx}
        isHoldingModifierDown={isHoldingModifierDown}
        onLeftClickClientCode={onLeftClickClientCode}
        onLeftClickProjectName={onLeftClickProjectName}
      />
    )
  }

  /** @param {import("@tanstack/react-table").CellContext <TData, string>} ctx */
  const ModuleCellTemplate = (ctx) => (
    <ModuleCell
      ctx={ctx}
      isHoldingModifierDown={isHoldingModifierDown}
      onLeftClickModuleName={onLeftClickModuleName}
    />
  )

  /** @param {import("@tanstack/react-table").CellContext <TData, string>} ctx */
  const CategoryCellTemplate = (ctx) => (
    <CategoryCell
      ctx={ctx}
      isHoldingModifierDown={isHoldingModifierDown}
      onLeftClickCategoryName={onLeftClickCategoryName}
    />
  )

  /** @param {import("@tanstack/react-table").CellContext <TData, string>} ctx */
  const TaskCellTemplate = (ctx) => <TaskCell ctx={ctx} />

  /** @param {import("@tanstack/react-table").CellContext <TData, number>} ctx */
  const SisuTimeCellTemplate = (ctx) => <SisuTimeCell ctx={ctx} />

  const SisuTimeFooterTemplate = () => numberFormatter(totals?.sisuTime)

  const BilledTimeFooterTemplate = () => (
    <strong className="font-bold">
      {totals?.billedTime ? numberFormatter(totals.billedTime) : "0"}
    </strong>
  )

  const TimeDifferenceFooterTemplate = () => (
    <strong className="flex gap-1 font-bold">
      {totals?.timeDifference ? numberFormatter(totals.timeDifference) : "0"}
    </strong>
  )

  /** @param {import("@tanstack/react-table").CellContext <TData, unknown>} ctx */
  const ActionsCellTemplate = (ctx) => (
    <ActionsCell
      ctx={ctx}
      userIsAdmin={userIsAdmin}
      updatingTask={updatingTask}
      contextMenuRef={contextMenuRef}
      setContextMenuTarget={setContextMenuTarget}
    />
  )

  return {
    DateCellTemplate,
    ProjectCodeCellTemplate,
    ModuleCellTemplate,
    CategoryCellTemplate,
    TaskCellTemplate,
    SisuTimeCellTemplate,
    SisuTimeFooterTemplate,
    BilledTimeFooterTemplate,
    TimeDifferenceFooterTemplate,
    ActionsCellTemplate,
  }
}
